import { selectUserInfo } from "features/user/userSlice";
import { Grid, TextField, Tooltip } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ModelProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Model } from "assets/SideBarIcons/car-model.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useEffect, useRef, useState } from "react";
import { GetModelByIdResponse } from "subModule/src/services/model/interface";
import Loader from "layout/Loader";
import DynamicSelect from "common/Select/Select";
import { ReactComponent as ModalBtn } from "assets/Modal/ModalBtn.svg";
import FormDialog from "common/Modal/Modal";
import MakeModal from "pages/Make/MakeModal";
import { useGetMakeDropdownService } from "subModule/src/services/make/useMake";
import ModelTabs from "./Modeltabs";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import CommonCheckbox from "common/CheckBox/Checkbox";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";

interface FormData {
  [key: string]: any;
}

interface ModelProps {
  setAddNew?: any;
  makesRefetch?: any;
  selectedMakeId?: any;
  fetchModels?: (makeId: number) => void; // Pass fetchModels function
  selectedMakeTitle?: string;
}
export default function ModelModal(props: ModelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, dirtyFields, isDirty },
    reset,
  } = useForm<FormData>({ defaultValues: {} });
  const loggedInUserData = useSelector(selectUserInfo);
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const isArabicLanguage = (lang?: string) => {
    const l = (lang || "").toLowerCase();
    return l === "arabic" || l === "ar" || l.includes("arab");
  };
  const localizedModelPlaceholder = (lang?: string) =>
    isArabicLanguage(lang) ? t("phModelAr") : t("phModelEn");
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "vehicles",
    moduleLabelKey: "Models",
    moduleHref: "/models",
    entityFallbackLabelKey: "title",
    isEdit: !!params?.id && !props?.setAddNew,
  });
  const [loading, setLoading] = useState<boolean>(
    params?.id && !props?.setAddNew ? true : false,
  );
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    params?.id && !props?.setAddNew ? false : true,
  );
  const [addNew, setAddNew] = useState<boolean>(false);
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const { data: makesData, refetch: makesRefetch } = useGetMakeDropdownService({
    pageNumber: pageNo,
    size: 50,
    title: dropdownSearchTerm,
  });
  const makes = (makesData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );

  useEffect(() => {
    if (props.selectedMakeId) {
      setValue("makeId", {
        value: props.selectedMakeId,
        label: props.selectedMakeTitle,
      });
    }
  }, [props.selectedMakeId, setValue]);
  useEffect(() => {
    makesRefetch();
    if (params?.id && !props?.setAddNew) {
      if (loading) getByID();
    }
  }, [params]);
  useEffect(() => {
    if (isDirty && params?.id) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);
  const getByID = () => {
    ModelProfileService.getModelByID(+(params as any)?.id)
      .then((res: GetModelByIdResponse) => {
        if (res.success) {
          captureLabel(res?.data?.title);
          reset({
            ...(res?.data || {}),
            makeId: { value: res.data?.makeId, label: res?.data?.makeTitle },
            isDefault: res?.data?.default,
          });
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);
    if (params?.id && !props?.setAddNew) {
      delete data.default;
      ModelProfileService.updateModel(+params?.id, {
        ...data,
        makeId: data.makeId.value,
      })
        .then((response: any) => {
          if (response.success) {
            captureLabel(data?.title);
            getByID();
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={t("model") + " " + t("updated") + " " + t("successfully")}
              />,
              { variant: "success" },
            );

            if (props.selectedMakeId && props.fetchModels) {
              props.fetchModels(props.selectedMakeId);
            }
          } else {
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
            setIsBtnEnabled(true);
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    } else {
      ModelProfileService.createModel({ ...data, makeId: data.makeId.value })
        .then((response: any) => {
          if (response.success) {
            if (props.selectedMakeId && props.fetchModels) {
              // Call fetchModels with the selected makeId
              props.fetchModels(props.selectedMakeId);
            }
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={t("model") + " " + t("created") + " " + t("successfully")}
              />,
              { variant: "success" },
            );
            if (props?.setAddNew) {
              props?.setAddNew(false);
              props.makesRefetch();
            } else navigate("/models");
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    }
  };
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setPageNo(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setPageNo(pageNo + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (typeName === "makes")
        makesRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };
  return (
    <>
      {props?.setAddNew ? null : (
        <ComponentHeaderTwo
          showBtn={true}
          showSaveBtn={true}
          onClick={() => navigate("/models")}
          onSubmit={handleSubmit(onSubmit)}
          heading={
            params?.id && !props?.setAddNew ? t("models") : t("addModels")
          }
          icon={<Model height={28} width={28} />}
          btnText={"back"}
          btnType={"back"}
          isBtnEnabled={IsBtnEnabled}
          showCreateBtn={checkPrivileges("ROLE_MODEL_CREATE")}
          breadcrumbItems={breadcrumbItems}
        />
      )}
      <div className={`${params?.id && !props?.setAddNew ? "flex" : ""}`}>
        {params?.id && !props?.setAddNew ? (
          <ModelTabs params={params} selectedIndex={0} />
        ) : null}
        {loading ? (
          <Loader />
        ) : (
          <SimpleModal
            visable={true}
            title={
              params?.id && !props?.setAddNew ? t("models") : t("addModels")
            }
            isButtonVisable={true}
            isVisableBtn={IsBtnEnabled}
            btnclosetext="Back"
            buttonText={params?.id && !props?.setAddNew ? "update" : "submit"}
            attBtnNotshow={false}
            notVisableBackbtn={true}
            showPortList={handleSubmit(onSubmit)}
            formSubmit={handleSubmit(onSubmit)}
            entityPopup={props?.setAddNew}
            padding={!props?.setAddNew}
            content={
              <Grid
                container
                component="form"
                id="form"
                className="mb-2"
                spacing={3}
                onSubmit={handleSubmit(onSubmit)}
              >
                <Grid
                  item
                  xs={12}
                  md={3}
                  className="grid"
                  sx={{
                    gridTemplateColumns:
                      checkPrivileges("ROLE_MAKE_CREATE") && !props?.setAddNew
                        ? "85% 10%"
                        : "100%",
                    "& .addNew path": { fill: themeColors?.primary },
                  }}
                >
                  <Controller
                    name="makeId"
                    control={control}
                    defaultValue={null}
                    rules={{ required: t("This field is required.") }}
                    render={({ field }) => (
                      <DynamicSelect
                        isDisabled={props?.selectedMakeId}
                        label={t("make") + " *"}
                        {...field}
                        options={makes}
                        isClearable
                        isSearchable
                        defaultPlaceholder="phSelectMake"
                        isLoading={searchLoading === "makes"}
                        onChange={(selectedOption: any) =>
                          field.onChange(selectedOption)
                        }
                        onInputChange={(
                          search: string,
                          { action }: { action: string },
                        ) => {
                          if (
                            action === "input-change" ||
                            (action !== "set-value" &&
                              action !== "menu-close" &&
                              action !== "input-blur")
                          )
                            handleDropdownActions("makes", "searchIt", search);
                          else if (
                            action === "input-blur" ||
                            action === "menu-close"
                          ) {
                            if (dropdownSearchTerm || pageNo > 1)
                              handleDropdownActions("makes", "searchIt", "");
                            else setDropdownSearchTerm("");
                            setPageNo(1);
                          }
                        }}
                        onMenuScrollToBottom={() => {
                          if (!(makesData?.pages[0]?.data as any).last)
                            handleDropdownActions("makes", "loadNext");
                        }}
                        error={errors.makeId?.message as string}
                        page={pageNo}
                      />
                    )}
                  />
                  {checkPrivileges("ROLE_MAKE_CREATE") && !props?.setAddNew && (
                    <Tooltip
                      onClick={() => setAddNew(true)}
                      title={t("addMake")}
                    >
                      <ModalBtn className="mt-5 mx-2 mb-2 cursor-pointer h-[38px] addNew" />
                    </Tooltip>
                  )}
                  {addNew && (
                    <FormDialog
                      open={addNew}
                      onClose={(event, reason) =>
                        event && reason !== "backdropClick"
                          ? setAddNew(false)
                          : ""
                      }
                      title={t("addMake")}
                      createPopup={true}
                      fullWidth
                      hideActions={true}
                      content={
                        <MakeModal
                          setAddNew={setAddNew}
                          makesRefetch={makesRefetch}
                        />
                      }
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("title", {
                      validate: {
                        required: (value) =>
                          value.trim() !== "" || t("This field is required."),
                      },
                    })}
                    label={`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`}
                    placeholder={localizedModelPlaceholder(primaryLanguage)}
                    title={`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`
                      .replace(/\s+/g, " ")
                      .trim()}
                    fullWidth
                    error={!!errors.title}
                    helperText={errors.title?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("secondaryTitle")}
                    label={`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`}
                    placeholder={localizedModelPlaceholder(secondaryLanguage)}
                    title={`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`
                      .replace(/\s+/g, " ")
                      .trim()}
                    fullWidth
                    error={!!errors.secondaryTitle}
                    helperText={errors.secondaryTitle?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                {checkPrivileges("ROLE_MANAGE_DEFAULT_MODEL") ? (
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="isDefault"
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <div>
                          <CommonCheckbox
                            {...field}
                            label={t("isDefault")}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) => field.onChange(event.target.checked)}
                            checked={field.value}
                            color={themeColors?.primary}
                          />
                        </div>
                      )}
                    />
                  </Grid>
                ) : null}
              </Grid>
            }
          />
        )}
      </div>
    </>
  );
}
